function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { debounce, throttle, prefersReducedMotion } from '@theme/utilities';

/**
 * Timeout duration (in milliseconds) after which scroll is considered to have ended.
 * @constant {number}
 */
const SCROLL_END_TIMEOUT = 50;

/**
 * Class representing a Scroller that handles smooth scrolling and detects scroll end events.
 *
 * @class
 */
var _promise = _classPrivateFieldLooseKey("promise");
var _resolve = _classPrivateFieldLooseKey("resolve");
var _throttledCallback = _classPrivateFieldLooseKey("throttledCallback");
var _endCallback = _classPrivateFieldLooseKey("endCallback");
var _onScrollInit = _classPrivateFieldLooseKey("onScrollInit");
var _onScrollEnd = _classPrivateFieldLooseKey("onScrollEnd");
var _userEvent = _classPrivateFieldLooseKey("userEvent");
var _ignore = _classPrivateFieldLooseKey("ignore");
var _isScrolling = _classPrivateFieldLooseKey("isScrolling");
var _scroll = _classPrivateFieldLooseKey("scroll");
var _edge = _classPrivateFieldLooseKey("edge");
var _setup = _classPrivateFieldLooseKey("setup");
var _reset = _classPrivateFieldLooseKey("reset");
var _handleScroll = _classPrivateFieldLooseKey("handleScroll");
var _handleScrollEnd = _classPrivateFieldLooseKey("handleScrollEnd");
export class Scroller {
  /**
   * Creates a Scroller instance.
   *
   * @param {HTMLElement} element - The element to apply scrolling to.
   * @param {Object} options - The options for the scroller.
   * @param {() => void} options.onScroll - Function to call while scrolling and after scrolling ends.
   * @param {() => void} [options.onScrollStart] - Function to call when scrolling starts.
   * @param {() => void} [options.onScrollEnd] - Function to call after scrolling ends.
   */
  constructor(element, _options) {
    /**
     * Sets up the scroll end promise if not already set.
     */
    Object.defineProperty(this, _setup, {
      value: _setup2
    });
    /**
     * Gets the scroll edge property ('Left' or 'Top') based on the axis.
     * @returns {'Left' | 'Top'}
     */
    Object.defineProperty(this, _edge, {
      get: _get_edge,
      set: void 0
    });
    /**
     * Scrolls the element.
     * @param {Object} options - The options for the scroll.
     * @param {'scrollTo' | 'scrollBy'} options.method - The method to use to scroll.
     * @param {number} options.value - The value to scroll to.
     * @param {boolean} [options.instant] - Whether to scroll instantly.
     */
    Object.defineProperty(this, _scroll, {
      value: _scroll2
    });
    /**
     * The element to apply scrolling to.
     * @type {HTMLElement}
     */
    this.element = void 0;
    /**
     * Promise that resolves when scrolling ends.
     * @type {Promise<void>|undefined}
     */
    Object.defineProperty(this, _promise, {
      writable: true,
      value: undefined
    });
    /**
     * Function to resolve the scroll end promise.
     * @type {Function|undefined}
     */
    Object.defineProperty(this, _resolve, {
      writable: true,
      value: undefined
    });
    /**
     * Callback function to call while user is scrolling, throttled every 50ms.
     * @type {() => void}
     */
    Object.defineProperty(this, _throttledCallback, {
      writable: true,
      value: void 0
    });
    /**
     * Callback function invoked after user scroll ends.
     * @type {() => void}
     */
    Object.defineProperty(this, _endCallback, {
      writable: true,
      value: void 0
    });
    /**
     * Callback function invoked when scrolling starts, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    Object.defineProperty(this, _onScrollInit, {
      writable: true,
      value: void 0
    });
    /**
     * Callback function invoked when scrolling ends, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    Object.defineProperty(this, _onScrollEnd, {
      writable: true,
      value: void 0
    });
    /**
     * Whether the scroll was triggered by a user event.
     * @type {boolean}
     */
    Object.defineProperty(this, _userEvent, {
      writable: true,
      value: true
    });
    /**
     * Whether the next scroll event should be ignored.
     * @type {boolean}
     */
    Object.defineProperty(this, _ignore, {
      writable: true,
      value: false
    });
    /**
     * Whether the element is currently scrolling.
     * @type {boolean}
     */
    Object.defineProperty(this, _isScrolling, {
      writable: true,
      value: false
    });
    Object.defineProperty(this, _reset, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _handleScrollEnd)[_handleScrollEnd].cancel();
        _classPrivateFieldLooseBase(this, _resolve)[_resolve]?.();
        _classPrivateFieldLooseBase(this, _promise)[_promise] = undefined;
        _classPrivateFieldLooseBase(this, _resolve)[_resolve] = undefined;
        _classPrivateFieldLooseBase(this, _userEvent)[_userEvent] = true;
        _classPrivateFieldLooseBase(this, _ignore)[_ignore] = false;
      }
    });
    /**
     * Event handler for the 'scroll' event.
     */
    Object.defineProperty(this, _handleScroll, {
      writable: true,
      value: () => {
        if (!_classPrivateFieldLooseBase(this, _isScrolling)[_isScrolling]) {
          _classPrivateFieldLooseBase(this, _onScrollInit)[_onScrollInit]?.();
          _classPrivateFieldLooseBase(this, _isScrolling)[_isScrolling] = true;
        }
        if (_classPrivateFieldLooseBase(this, _ignore)[_ignore]) {
          _classPrivateFieldLooseBase(this, _reset)[_reset]();
          _classPrivateFieldLooseBase(this, _handleScrollEnd)[_handleScrollEnd](false);
          return;
        }
        const userEvent = _classPrivateFieldLooseBase(this, _userEvent)[_userEvent];
        _classPrivateFieldLooseBase(this, _setup)[_setup]();
        if (userEvent) _classPrivateFieldLooseBase(this, _throttledCallback)[_throttledCallback]();
        _classPrivateFieldLooseBase(this, _handleScrollEnd)[_handleScrollEnd](userEvent);
      }
    });
    /**
     * Handler called when scrolling has ended.
     */
    Object.defineProperty(this, _handleScrollEnd, {
      writable: true,
      value: debounce(
      /**
       * @param {boolean} userEvent
       */
      userEvent => {
        _classPrivateFieldLooseBase(this, _resolve)[_resolve]?.();
        if (userEvent) _classPrivateFieldLooseBase(this, _endCallback)[_endCallback]();
        _classPrivateFieldLooseBase(this, _reset)[_reset]();
        if (_classPrivateFieldLooseBase(this, _isScrolling)[_isScrolling]) {
          _classPrivateFieldLooseBase(this, _onScrollEnd)[_onScrollEnd]?.();
          _classPrivateFieldLooseBase(this, _isScrolling)[_isScrolling] = false;
        }
      }, SCROLL_END_TIMEOUT)
    });
    _classPrivateFieldLooseBase(this, _throttledCallback)[_throttledCallback] = throttle(_options.onScroll, SCROLL_END_TIMEOUT);
    _classPrivateFieldLooseBase(this, _endCallback)[_endCallback] = _options.onScroll;
    _classPrivateFieldLooseBase(this, _onScrollInit)[_onScrollInit] = _options.onScrollStart;
    _classPrivateFieldLooseBase(this, _onScrollEnd)[_onScrollEnd] = _options.onScrollEnd;
    this.element = element;
    this.element.addEventListener('scroll', _classPrivateFieldLooseBase(this, _handleScroll)[_handleScroll]);
  }

  /**
   * Scrolls to a specific position or element.
   * @param {number | HTMLElement} input - The position in pixels or an element to scroll to.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  async to(input, options) {
    let value;
    if (input instanceof HTMLElement) {
      const paddingStart = calculatePaddingStart(this.element, this.axis);
      value = input[`offset${_classPrivateFieldLooseBase(this, _edge)[_edge]}`] - paddingStart;
    } else {
      value = input;
    }
    const currentPosition = this.element[`scroll${_classPrivateFieldLooseBase(this, _edge)[_edge]}`];
    const willChange = currentPosition !== value;
    if (willChange) {
      _classPrivateFieldLooseBase(this, _scroll)[_scroll]({
        ...options,
        method: 'scrollTo',
        value
      });
    } else if (_classPrivateFieldLooseBase(this, _isScrolling)[_isScrolling]) {
      // If the scroll has started but then it's released in the same original position,
      // the scroll event will not fire, so we need to manually trigger the scroll end.
      _classPrivateFieldLooseBase(this, _handleScrollEnd)[_handleScrollEnd](false);
    }
  }

  /**
   * Scrolls by a certain number of pixels.
   * @param {number} value - The number of pixels to scroll by.
   * @param {Object} [options] - Options for the scroll.
   * @param {boolean} [options.instant] - Whether to scroll instantly.
   */
  by(value, options) {
    _classPrivateFieldLooseBase(this, _scroll)[_scroll]({
      ...options,
      method: 'scrollBy',
      value
    });
  }
  /**
   * Gets the scrolling axis ('x' or 'y') based on the element's dimensions.
   * @type {'x' | 'y'}
   * @readonly
   */
  get axis() {
    return getScrollAxis(this.element);
  }

  /**
   * Promise that resolves when scrolling ends.
   * @type {Promise<void>}
   * @readonly
   */
  get finished() {
    return _classPrivateFieldLooseBase(this, _promise)[_promise] ?? Promise.resolve();
  }
  /**
   * Sets the scroll snap behavior of the element.
   * @param {boolean} value - Whether to enable scroll snap.
   */
  set snap(value) {
    // Changing the snap behavior will trigger a scroll event, which we should ignore
    _classPrivateFieldLooseBase(this, _ignore)[_ignore] = true;
    this.element.style.setProperty('scroll-snap-type', value ? `${this.axis} mandatory` : 'none');
  }

  /**
   * Destroys the Scroller instance.
   */
  destroy() {
    this.element.removeEventListener('scroll', _classPrivateFieldLooseBase(this, _handleScroll)[_handleScroll]);
  }
}

/**
 * Gets the scroll axis ('x' or 'y') based on the element's dimensions.
 * @param {HTMLElement} el - The element to get the scroll axis of.
 * @returns {'x' | 'y'}
 */
function _scroll2(options) {
  const {
    method,
    value,
    instant = prefersReducedMotion()
  } = options;
  _classPrivateFieldLooseBase(this, _reset)[_reset]();
  _classPrivateFieldLooseBase(this, _ignore)[_ignore] = instant;
  _classPrivateFieldLooseBase(this, _userEvent)[_userEvent] = false;

  // Check if we need to scroll at all
  const currentPosition = this.element[`scroll${_classPrivateFieldLooseBase(this, _edge)[_edge]}`];
  const targetPosition = method === 'scrollBy' ? currentPosition + value : value;
  const scrollDistance = Math.abs(targetPosition - currentPosition);

  // If the distance is negligible, don't scroll and resolve immediately
  if (scrollDistance < 1) {
    return Promise.resolve();
  }
  if (!instant) _classPrivateFieldLooseBase(this, _setup)[_setup]();
  this.element[method]({
    [_classPrivateFieldLooseBase(this, _edge)[_edge].toLowerCase()]: value,
    behavior: instant ? 'instant' : 'smooth'
  });
}
function _get_edge() {
  return this.axis === 'x' ? 'Left' : 'Top';
}
function _setup2() {
  if (_classPrivateFieldLooseBase(this, _promise)[_promise]) {
    return;
  }
  _classPrivateFieldLooseBase(this, _promise)[_promise] = new Promise(resolve => _classPrivateFieldLooseBase(this, _resolve)[_resolve] = resolve);
}
function getScrollAxis(el) {
  if (el.scrollHeight > el.clientHeight && el.scrollWidth === el.clientWidth) {
    return 'y';
  }
  if (el.scrollWidth > el.clientWidth && el.scrollHeight === el.clientHeight) {
    return 'x';
  }
  return el.scrollWidth > el.scrollHeight ? 'x' : 'y';
}

/**
 * Calculates the padding-start around an element to update the scroll offset.
 * @param {HTMLElement} element - The element to calculate the padding-start of.
 * @param {'x' | 'y'} axis - The axis to calculate the padding-start of.
 * @returns {number} The padding-start in pixels.
 */
function calculatePaddingStart(element, axis) {
  const computedStyle = getComputedStyle(element);
  const value = axis === 'x' ? computedStyle.paddingInlineStart : computedStyle.paddingBlockStart;
  return parseFloat(value);
}

/**
 * Scrolls an element into view.
 * @param {Element} element - The element to scroll into view.
 * @param {Object} options - The options for the scroll.
 * @param {ScrollBehavior} [options.behavior='smooth'] - The behavior of the scroll.
 * @param {'start' | 'center' | 'end'} [options.block='start'] - The block alignment of the element.
 * @param {'start' | 'center' | 'end'} [options.inline='start'] - The inline alignment of the element.
 * @param {Element} [options.ancestor] - The ancestor element to scroll into view.
 */
export function scrollIntoView(element, {
  ancestor,
  behavior = 'smooth',
  block = 'start',
  inline = 'start'
} = {}) {
  if (!ancestor) {
    return element.scrollIntoView({
      behavior,
      block,
      inline
    });
  }
  const elemRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();

  /**
   * Calculates the scroll offset for an element.
   * @param {'start' | 'center' | 'end'} alignment - The alignment of the element.
   * @param {number} ancestorStart - The start of the ancestor element.
   * @param {number} ancestorLength - The length of the ancestor element.
   * @param {number} elemStart - The start of the element.
   * @param {number} elemLength - The length of the element.
   * @param {number} currentScroll - The current scroll position.
   * @returns {number} The scroll offset.
   */
  const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
    switch (alignment) {
      case 'start':
        return currentScroll + elemStart - ancestorStart;
      case 'center':
        return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
      case 'end':
        return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
      default:
        return currentScroll;
    }
  };
  const scrollTop = ancestor.scrollHeight > ancestor.clientHeight ? calculateScrollOffset(block, ancestorRect.top, ancestor.clientHeight, elemRect.top, elemRect.height, ancestor.scrollTop) : ancestor.scrollTop;
  const scrollLeft = ancestor.scrollWidth > ancestor.clientWidth ? calculateScrollOffset(inline, ancestorRect.left, ancestor.clientWidth, elemRect.left, elemRect.width, ancestor.scrollLeft) : ancestor.scrollLeft;
  ancestor.scrollTo({
    top: scrollTop,
    left: scrollLeft,
    behavior
  });
}

// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. Slideshow)
var _rafId = _classPrivateFieldLooseKey("rafId");
var _handleScroll2 = _classPrivateFieldLooseKey("handleScroll");
var _update = _classPrivateFieldLooseKey("update");
var _resizeObserver = _classPrivateFieldLooseKey("resizeObserver");
class ScrollHint extends HTMLElement {
  constructor(...args) {
    super(...args);
    /** @type {number | null} */
    Object.defineProperty(this, _rafId, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _handleScroll2, {
      writable: true,
      value: () => {
        // Throttle scroll updates with requestAnimationFrame to avoid layout thrashing
        if (_classPrivateFieldLooseBase(this, _rafId)[_rafId] !== null) return;
        _classPrivateFieldLooseBase(this, _rafId)[_rafId] = requestAnimationFrame(() => {
          _classPrivateFieldLooseBase(this, _rafId)[_rafId] = null;
          _classPrivateFieldLooseBase(this, _update)[_update]();
        });
      }
    });
    Object.defineProperty(this, _update, {
      writable: true,
      value: () => {
        const {
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollLeft,
          scrollWidth,
          clientWidth
        } = this;
        const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
        const scrollPercentage = scrollDirection === 'vertical' ? scrollTop / (scrollHeight - clientHeight) : scrollLeft / (scrollWidth - clientWidth);
        this.style.maskImage = Number.isNaN(scrollPercentage) ? '' : `linear-gradient(
        to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
        transparent ${scrollPercentage > 0 ? 1 : 0}%,
        black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
        black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
        transparent 100%
      )`;
      }
    });
    Object.defineProperty(this, _resizeObserver, {
      writable: true,
      value: new ResizeObserver(_classPrivateFieldLooseBase(this, _update)[_update])
    });
  }
  connectedCallback() {
    this.addEventListener('scroll', _classPrivateFieldLooseBase(this, _handleScroll2)[_handleScroll2]);
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].observe(this);
  }
  disconnectedCallback() {
    this.removeEventListener('scroll', _classPrivateFieldLooseBase(this, _handleScroll2)[_handleScroll2]);
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
    if (_classPrivateFieldLooseBase(this, _rafId)[_rafId] !== null) {
      cancelAnimationFrame(_classPrivateFieldLooseBase(this, _rafId)[_rafId]);
      _classPrivateFieldLooseBase(this, _rafId)[_rafId] = null;
    }
  }
}
if (!customElements.get('scroll-hint')) {
  customElements.define('scroll-hint', ScrollHint);
}
